import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import useElementToPdf from '../../utils/useElementToPdf';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { DirhamIconColored } from '../../components/DirhamIcon';
import ServicesCatalogSheet from '../../components/ServicesCatalogSheet';
import {
  Plus,
  Edit2,
  Trash2,
  Scissors,
  Sparkles,
  Hand,
  Check,
  X,
  Clock,
  Tag,
  Package,
  Settings,
  Eye,
  EyeOff,
  Loader2,
  AlertCircle,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Edit3,
  TrendingUp,
  Printer,
} from 'lucide-react';
import { PriceChangeRequestModal, PriceChangeRequestsPanel } from '../../components/PriceChangeRequests';
import CategoryModal from '../../components/services/CategoryModal';
import ServiceModal from '../../components/services/ServiceModal';
import OfferModal from '../../components/services/OfferModal';

const ServicesManagement = () => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const [user, setUser] = useState(null);
  
  // Data states
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [offers, setOffers] = useState([]);
  const [pendingOffers, setPendingOffers] = useState([]);
  const [pendingCategories, setPendingCategories] = useState([]);
  const [pendingServices, setPendingServices] = useState([]);
  const [displaySettings, setDisplaySettings] = useState({ show_prices: true });
  
  // Print catalog
  const catalogPrintRef = useRef(null);
  const handlePrintCatalog = useElementToPdf({
    contentRef: catalogPrintRef,
    documentTitle: `KE_Gents_Saloon_Catalog_${new Date().toISOString().slice(0, 10)}`,
    pageStyle: '@page { size: A4 portrait; margin: 0; } @media print { body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; margin: 0; padding: 0; } html, body { height: auto !important; } }',
  });
  
  // UI states
  const [activeTab, setActiveTab] = useState('categories');
  const [previewItem, setPreviewItem] = useState(null); // For preview modal
  const [previewType, setPreviewType] = useState(null); // 'offer', 'category', 'service'
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Modal states
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  // Form states
  const [categoryForm, setCategoryForm] = useState({
    name_ar: '', name_en: '', icon: 'scissors', display_order: 0, is_visible: true, show_price: true
  });
  const [serviceForm, setServiceForm] = useState({
    category_id: '', name_ar: '', name_en: '', description_ar: '', description_en: '',
    price: '', duration_minutes: 30, display_order: 0, is_visible: true, show_price: true
  });
  const [offerForm, setOfferForm] = useState({
    title_ar: '', title_en: '', description_ar: '', description_en: '',
    service_ids: [], original_price: '', discount_price: '',
    start_date: '', end_date: '', is_visible: true, show_price: true
  });
  
  // Expanded sections
  const [expandedCategory, setExpandedCategory] = useState(null);

  // Price change request state
  const [priceChangeService, setPriceChangeService] = useState(null);
  const [priceChangesRefreshKey, setPriceChangesRefreshKey] = useState(0);

  const icons = [
    { value: 'scissors', label: 'Scissors', Icon: Scissors },
    { value: 'sparkles', label: 'Sparkles', Icon: Sparkles },
    { value: 'hand', label: 'Hand', Icon: Hand },
    { value: 'tag', label: 'Tag', Icon: Tag },
    { value: 'package', label: 'Package', Icon: Package },
  ];

  useEffect(() => {
    fetchUser();
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUser = async () => {
    try {
      const res = await api.get('/auth/me');
      setUser(res.data);
    } catch (error) {
      console.error('Error fetching user:', error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [catRes, svcRes, offersRes, settingsRes] = await Promise.all([
        api.get('/services/categories/all'),
        api.get('/services/all'),
        api.get('/services/offers/all'),
        api.get('/services/display-settings')
      ]);
      setCategories(catRes.data);
      setServices(svcRes.data);
      setOffers(offersRes.data);
      setDisplaySettings(settingsRes.data);
      
      // Fetch pending offers for admin
      try {
        const [pendingOffersRes, pendingCatRes, pendingSvcRes] = await Promise.all([
          api.get('/services/offers/pending'),
          api.get('/services/categories/pending'),
          api.get('/services/pending')
        ]);
        setPendingOffers(pendingOffersRes.data);
        setPendingCategories(pendingCatRes.data);
        setPendingServices(pendingSvcRes.data);
      } catch (e) {
        // Not admin, ignore
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      showMessage('error', language === 'ar' ? 'خطأ في تحميل البيانات' : 'Error loading data');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 4000);
  };

  const getIconComponent = (iconName) => {
    const icon = icons.find(i => i.value === iconName);
    return icon ? icon.Icon : Scissors;
  };

  // ==================== CATEGORIES ====================
  const handleSeedDefaults = async () => {
    setSaving(true);
    try {
      await api.post('/services/seed-defaults');
      showMessage('success', language === 'ar' ? 'تم إضافة الأقسام الافتراضية' : 'Default categories added');
      fetchData();
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Error');
    } finally {
      setSaving(false);
    }
  };

  const openCategoryModal = (category = null) => {
    if (category) {
      setCategoryForm({
        name_ar: category.name_ar,
        name_en: category.name_en,
        icon: category.icon || 'scissors',
        display_order: category.display_order || 0,
        is_visible: category.is_visible !== false,
        show_price: category.show_price !== false
      });
      setEditingItem(category);
    } else {
      setCategoryForm({ name_ar: '', name_en: '', icon: 'scissors', display_order: 0, is_visible: true, show_price: true });
      setEditingItem(null);
    }
    setShowCategoryModal(true);
  };

  const saveCategory = async () => {
    if (!categoryForm.name_ar || !categoryForm.name_en) {
      showMessage('error', language === 'ar' ? 'يرجى ملء جميع الحقول' : 'Please fill all fields');
      return;
    }
    setSaving(true);
    try {
      if (editingItem) {
        await api.put(`/services/categories/${editingItem.id}`, categoryForm);
      } else {
        await api.post('/services/categories', categoryForm);
      }
      showMessage('success', language === 'ar' ? 'تم الحفظ بنجاح' : 'Saved successfully');
      setShowCategoryModal(false);
      fetchData();
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Error');
    } finally {
      setSaving(false);
    }
  };

  const deleteCategory = async (id) => {
    if (!window.confirm(language === 'ar' ? 'هل أنت متأكد من حذف هذا القسم؟' : 'Are you sure you want to delete this category?')) return;
    try {
      await api.delete(`/services/categories/${id}`);
      showMessage('success', language === 'ar' ? 'تم الحذف' : 'Deleted');
      fetchData();
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Error');
    }
  };

  // ==================== SERVICES ====================
  const openServiceModal = (service = null) => {
    if (service) {
      setServiceForm({
        category_id: service.category_id,
        name_ar: service.name_ar,
        name_en: service.name_en,
        description_ar: service.description_ar || '',
        description_en: service.description_en || '',
        price: service.price,
        duration_minutes: service.duration_minutes || 30,
        display_order: service.display_order || 0,
        is_visible: service.is_visible !== false,
        show_price: service.show_price !== false
      });
      setEditingItem(service);
    } else {
      setServiceForm({
        category_id: categories[0]?.id || '',
        name_ar: '', name_en: '', description_ar: '', description_en: '',
        price: '', duration_minutes: 30, display_order: 0, is_visible: true, show_price: true
      });
      setEditingItem(null);
    }
    setShowServiceModal(true);
  };

  const saveService = async () => {
    if (!serviceForm.category_id || !serviceForm.name_ar || !serviceForm.name_en || !serviceForm.price) {
      showMessage('error', language === 'ar' ? 'يرجى ملء الحقول المطلوبة' : 'Please fill required fields');
      return;
    }
    setSaving(true);
    try {
      const data = { ...serviceForm, price: parseFloat(serviceForm.price) };
      if (editingItem) {
        await api.put(`/services/${editingItem.id}`, data);
      } else {
        await api.post('/services', data);
      }
      showMessage('success', language === 'ar' ? 'تم الحفظ بنجاح' : 'Saved successfully');
      setShowServiceModal(false);
      fetchData();
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Error');
    } finally {
      setSaving(false);
    }
  };

  const deleteService = async (id) => {
    if (!window.confirm(language === 'ar' ? 'هل أنت متأكد من حذف هذه الخدمة؟' : 'Are you sure you want to delete this service?')) return;
    try {
      await api.delete(`/services/${id}`);
      showMessage('success', language === 'ar' ? 'تم الحذف' : 'Deleted');
      fetchData();
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Error');
    }
  };

  // ==================== APPROVAL FUNCTIONS ====================
  const approveCategory = async (id, approved, reason = '') => {
    try {
      await api.post(`/services/categories/${id}/approve`, { approved, rejection_reason: reason });
      showMessage('success', approved 
        ? (language === 'ar' ? 'تم اعتماد القسم' : 'Category approved')
        : (language === 'ar' ? 'تم رفض القسم' : 'Category rejected')
      );
      fetchData();
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Error');
    }
  };

  const approveService = async (id, approved, reason = '') => {
    try {
      await api.post(`/services/${id}/approve`, { approved, rejection_reason: reason });
      showMessage('success', approved 
        ? (language === 'ar' ? 'تم اعتماد الخدمة' : 'Service approved')
        : (language === 'ar' ? 'تم رفض الخدمة' : 'Service rejected')
      );
      fetchData();
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Error');
    }
  };

  // ==================== OFFERS ====================
  const openOfferModal = (offer = null) => {
    if (offer) {
      setOfferForm({
        title_ar: offer.title_ar,
        title_en: offer.title_en,
        description_ar: offer.description_ar || '',
        description_en: offer.description_en || '',
        service_ids: offer.service_ids || [],
        original_price: offer.original_price,
        discount_price: offer.discount_price,
        start_date: offer.start_date,
        end_date: offer.end_date
      });
      setEditingItem(offer);
    } else {
      setOfferForm({
        title_ar: '', title_en: '', description_ar: '', description_en: '',
        service_ids: [], original_price: '', discount_price: '',
        start_date: new Date().toISOString().split('T')[0],
        end_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
      });
      setEditingItem(null);
    }
    setShowOfferModal(true);
  };

  const saveOffer = async () => {
    if (!offerForm.title_ar || !offerForm.title_en || !offerForm.original_price || !offerForm.discount_price || !offerForm.start_date || !offerForm.end_date) {
      showMessage('error', language === 'ar' ? 'يرجى ملء الحقول المطلوبة' : 'Please fill required fields');
      return;
    }
    setSaving(true);
    try {
      const data = {
        ...offerForm,
        original_price: parseFloat(offerForm.original_price),
        discount_price: parseFloat(offerForm.discount_price)
      };
      if (editingItem) {
        await api.put(`/services/offers/${editingItem.id}`, data);
      } else {
        await api.post('/services/offers', data);
      }
      showMessage('success', language === 'ar' ? 'تم الحفظ بنجاح' : 'Saved successfully');
      setShowOfferModal(false);
      fetchData();
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Error');
    } finally {
      setSaving(false);
    }
  };

  const approveOffer = async (id, approved, reason = '') => {
    try {
      await api.post(`/services/offers/${id}/approve`, { approved, rejection_reason: reason });
      showMessage('success', approved 
        ? (language === 'ar' ? 'تم اعتماد العرض' : 'Offer approved')
        : (language === 'ar' ? 'تم رفض العرض' : 'Offer rejected')
      );
      fetchData();
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Error');
    }
  };

  const deleteOffer = async (id) => {
    if (!window.confirm(language === 'ar' ? 'هل أنت متأكد من حذف هذا العرض؟' : 'Are you sure you want to delete this offer?')) return;
    try {
      await api.delete(`/services/offers/${id}`);
      showMessage('success', language === 'ar' ? 'تم الحذف' : 'Deleted');
      fetchData();
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Error');
    }
  };

  // ==================== DISPLAY SETTINGS ====================
  const toggleShowPrices = async () => {
    try {
      const newValue = !displaySettings.show_prices;
      await api.put('/services/display-settings', { show_prices: newValue });
      setDisplaySettings({ show_prices: newValue });
      showMessage('success', language === 'ar' ? 'تم تحديث الإعدادات' : 'Settings updated');
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Error');
    }
  };

  const getServicesByCategory = (categoryId) => {
    return services.filter(s => s.category_id === categoryId && s.is_active);
  };

  const getCategoryName = (id) => {
    const cat = categories.find(c => c.id === id);
    return cat ? (language === 'ar' ? cat.name_ar : cat.name_en) : '';
  };

  const getServiceName = (id) => {
    const svc = services.find(s => s.id === id);
    return svc ? (language === 'ar' ? svc.name_ar : svc.name_en) : '';
  };

  const getOfferStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-yellow-500/20', text: 'text-yellow-500', label: language === 'ar' ? 'بانتظار الموافقة' : 'Pending' },
      approved: { bg: 'bg-green-500/20', text: 'text-green-500', label: language === 'ar' ? 'معتمد' : 'Approved' },
      rejected: { bg: 'bg-red-500/20', text: 'text-red-500', label: language === 'ar' ? 'مرفوض' : 'Rejected' }
    };
    return badges[status] || badges.pending;
  };

  const getCategoryIcon = (iconName) => {
    const icons = {
      scissors: <Scissors className="w-8 h-8 text-primary" />,
      sparkles: <Sparkles className="w-8 h-8 text-primary" />,
      hand: <Hand className="w-8 h-8 text-primary" />,
      tag: <Tag className="w-8 h-8 text-primary" />,
      package: <Package className="w-8 h-8 text-primary" />,
      settings: <Settings className="w-8 h-8 text-primary" />
    };
    return icons[iconName] || icons.scissors;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">
            {language === 'ar' ? 'الخدمات والعروض' : 'Services & Offers'}
          </h1>
          <p className="text-muted-foreground text-sm">
            {language === 'ar' ? 'إدارة خدمات الصالون والعروض الخاصة' : 'Manage salon services and special offers'}
          </p>
        </div>
        
        {user?.role === 'admin' && (
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              onClick={handlePrintCatalog}
              variant="outline"
              size="sm"
              className="gap-2 border-amber-500/40 hover:bg-amber-500/10"
              data-testid="print-catalog-btn"
              title={language === 'ar' ? 'تنزيل قائمة الأقسام والخدمات والعروض كملف PDF' : 'Download full catalog as PDF'}
            >
              <Printer className="w-4 h-4" />
              {language === 'ar' ? 'تنزيل القائمة (PDF)' : 'Download Catalog (PDF)'}
            </Button>
            <Button
              onClick={toggleShowPrices}
              variant="outline"
              size="sm"
              className="gap-2"
            >
              {displaySettings.show_prices ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              {displaySettings.show_prices 
                ? (language === 'ar' ? 'إخفاء الأسعار' : 'Hide Prices')
                : (language === 'ar' ? 'إظهار الأسعار' : 'Show Prices')
              }
            </Button>
          </div>
        )}
      </div>

      {/* Message */}
      {message.text && (
        <div className={`flex items-center gap-2 p-3 rounded-lg ${
          message.type === 'success' ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'
        }`}>
          {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          <span>{message.text}</span>
        </div>
      )}

      {/* Pending Offers Alert for Admin */}
      {user?.role === 'admin' && pendingOffers.length > 0 && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
          <div className="flex items-center gap-2 text-yellow-500 mb-2">
            <Clock className="w-5 h-5" />
            <span className="font-bold">
              {language === 'ar' ? `${pendingOffers.length} عرض بانتظار الموافقة` : `${pendingOffers.length} offers pending approval`}
            </span>
          </div>
          <div className="space-y-2">
            {pendingOffers.map(offer => (
              <div key={offer.id} className="flex items-center justify-between bg-card/50 p-3 rounded-lg">
                <div className="flex-1">
                  <p className="text-white font-medium">{language === 'ar' ? offer.title_ar : offer.title_en}</p>
                  <p className="text-sm text-muted-foreground">
                    {language === 'ar' ? 'بواسطة: ' : 'By: '}{offer.created_by_name}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => { setPreviewItem(offer); setPreviewType('offer'); }}
                    size="sm"
                    variant="outline"
                    className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button
                    onClick={() => approveOffer(offer.id, true)}
                    size="sm"
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Check className="w-4 h-4" />
                  </Button>
                  <Button
                    onClick={() => {
                      const reason = prompt(language === 'ar' ? 'سبب الرفض (اختياري):' : 'Rejection reason (optional):');
                      approveOffer(offer.id, false, reason || '');
                    }}
                    size="sm"
                    variant="destructive"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pending Categories Alert for Admin */}
      {user?.role === 'admin' && pendingCategories.length > 0 && (
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
          <div className="flex items-center gap-2 text-blue-500 mb-2">
            <Clock className="w-5 h-5" />
            <span className="font-bold">
              {language === 'ar' ? `${pendingCategories.length} قسم بانتظار الموافقة` : `${pendingCategories.length} categories pending approval`}
            </span>
          </div>
          <div className="space-y-2">
            {pendingCategories.map(cat => (
              <div key={cat.id} className="flex items-center justify-between bg-card/50 p-3 rounded-lg">
                <div className="flex-1">
                  <p className="text-white font-medium">{language === 'ar' ? cat.name_ar : cat.name_en}</p>
                  <p className="text-sm text-muted-foreground">
                    {language === 'ar' ? 'بواسطة: ' : 'By: '}{cat.created_by_name}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => { setPreviewItem(cat); setPreviewType('category'); }}
                    size="sm"
                    variant="outline"
                    className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button
                    onClick={() => approveCategory(cat.id, true)}
                    size="sm"
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Check className="w-4 h-4" />
                  </Button>
                  <Button
                    onClick={() => approveCategory(cat.id, false)}
                    size="sm"
                    variant="destructive"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pending Services Alert for Admin */}
      {user?.role === 'admin' && pendingServices.length > 0 && (
        <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
          <div className="flex items-center gap-2 text-purple-500 mb-2">
            <Clock className="w-5 h-5" />
            <span className="font-bold">
              {language === 'ar' ? `${pendingServices.length} خدمة بانتظار الموافقة` : `${pendingServices.length} services pending approval`}
            </span>
          </div>
          <div className="space-y-2">
            {pendingServices.map(svc => (
              <div key={svc.id} className="flex items-center justify-between bg-card/50 p-3 rounded-lg">
                <div className="flex-1">
                  <p className="text-white font-medium">{language === 'ar' ? svc.name_ar : svc.name_en}</p>
                  <p className="text-sm text-muted-foreground">
                                                            {language === 'ar' ? 'بواسطة: ' : 'By: '}{svc.created_by_name} | {svc.price} <DirhamIconColored className="w-3 h-3 inline" color="primary" />
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => { setPreviewItem(svc); setPreviewType('service'); }}
                    size="sm"
                    variant="outline"
                    className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button
                    onClick={() => approveService(svc.id, true)}
                    size="sm"
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Check className="w-4 h-4" />
                  </Button>
                  <Button
                    onClick={() => approveService(svc.id, false)}
                    size="sm"
                    variant="destructive"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-white/10 pb-2 flex-wrap">
        {[
          { id: 'categories', label: language === 'ar' ? 'الأقسام' : 'Categories' },
          { id: 'services', label: language === 'ar' ? 'الخدمات' : 'Services' },
          { id: 'offers', label: language === 'ar' ? 'العروض' : 'Offers' },
          { id: 'price_changes', label: language === 'ar' ? 'تعديلات الأسعار' : 'Price Changes' }
        ].map(tab => (
          <Button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            variant={activeTab === tab.id ? 'default' : 'ghost'}
            className={activeTab === tab.id ? 'bg-primary' : ''}
            data-testid={`tab-${tab.id}`}
          >
            {tab.label}
          </Button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'categories' && (
        <div className="space-y-4">
          <div className="flex gap-2">
            <Button onClick={() => openCategoryModal()} className="gap-2 bg-primary">
              <Plus className="w-4 h-4" />
              {language === 'ar' ? 'إضافة قسم' : 'Add Category'}
            </Button>
            {categories.length === 0 && (
              <Button onClick={handleSeedDefaults} variant="outline" className="gap-2" disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Package className="w-4 h-4" />}
                {language === 'ar' ? 'إضافة الأقسام الافتراضية' : 'Add Default Categories'}
              </Button>
            )}
          </div>

          <div className="grid gap-4">
            {categories.filter(c => c.is_active).map(category => {
              const IconComponent = getIconComponent(category.icon);
              const categoryServices = getServicesByCategory(category.id);
              const isExpanded = expandedCategory === category.id;
              
              return (
                <Card key={category.id} className="bg-card border-white/10 overflow-hidden">
                  <div 
                    className="p-4 flex items-center justify-between cursor-pointer hover:bg-white/5"
                    onClick={() => setExpandedCategory(isExpanded ? null : category.id)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-primary/20 rounded-xl flex items-center justify-center">
                        <IconComponent className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-bold text-white">
                            {language === 'ar' ? category.name_ar : category.name_en}
                          </h3>
                          {/* Visibility indicators */}
                          {user?.role === 'admin' && (
                            <div className="flex items-center gap-1">
                              {category.is_visible === false ? (
                                <span className="text-xs px-1.5 py-0.5 rounded bg-red-500/20 text-red-400 flex items-center gap-1">
                                  <EyeOff className="w-3 h-3" />
                                  {language === 'ar' ? 'مخفي' : 'Hidden'}
                                </span>
                              ) : null}
                              {category.show_price === false ? (
                                <span className="text-xs px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-400">
                                  {language === 'ar' ? 'السعر مخفي' : 'Price hidden'}
                                </span>
                              ) : null}
                            </div>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {categoryServices.length} {language === 'ar' ? 'خدمة' : 'services'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {user?.role === 'admin' && (
                        <>
                          <Button
                            onClick={(e) => { e.stopPropagation(); openCategoryModal(category); }}
                            size="sm"
                            variant="ghost"
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            onClick={(e) => { e.stopPropagation(); deleteCategory(category.id); }}
                            size="sm"
                            variant="ghost"
                            className="text-red-500 hover:text-red-400"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      {isExpanded ? <ChevronUp className="w-5 h-5 text-muted-foreground" /> : <ChevronDown className="w-5 h-5 text-muted-foreground" />}
                    </div>
                  </div>
                  
                  {isExpanded && categoryServices.length > 0 && (
                    <div className="border-t border-white/10 p-4 bg-black/20">
                      <div className="space-y-2">
                        {categoryServices.map(service => (
                          <div key={service.id} className="flex items-center justify-between bg-card/50 p-3 rounded-lg">
                            <div>
                              <p className="text-white font-medium">
                                {language === 'ar' ? service.name_ar : service.name_en}
                              </p>
                              {displaySettings.show_prices && (
                                                                                                <p className="text-sm text-primary font-bold">{service.price} <DirhamIconColored className="w-3 h-3 inline" color="primary" /></p>
                              )}
                            </div>
                            <div className="flex gap-2">
                              <Button onClick={() => openServiceModal(service)} size="sm" variant="ghost">
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              {user?.role === 'admin' && (
                                <Button onClick={() => deleteService(service.id)} size="sm" variant="ghost" className="text-red-500">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </Card>
              );
            })}
            
            {categories.filter(c => c.is_active).length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>{language === 'ar' ? 'لا توجد أقسام. أضف قسم جديد أو استخدم الأقسام الافتراضية.' : 'No categories. Add a new category or use defaults.'}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'services' && (
        <div className="space-y-4">
          <Button onClick={() => openServiceModal()} className="gap-2 bg-primary">
            <Plus className="w-4 h-4" />
            {language === 'ar' ? 'إضافة خدمة' : 'Add Service'}
          </Button>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {services.filter(s => s.is_active).map(service => (
              <Card key={service.id} className="bg-card border-white/10 p-4">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <p className="text-xs text-primary font-medium">{getCategoryName(service.category_id)}</p>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-bold text-white">
                        {language === 'ar' ? service.name_ar : service.name_en}
                      </h3>
                      {/* Visibility indicators */}
                      {user?.role === 'admin' && (
                        <>
                          {service.is_visible === false && (
                            <span className="text-xs px-1.5 py-0.5 rounded bg-red-500/20 text-red-400 flex items-center gap-1">
                              <EyeOff className="w-3 h-3" />
                            </span>
                          )}
                          {service.show_price === false && (
                            <span className="text-xs px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-400">
                              {language === 'ar' ? '₽' : '₽'}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  {displaySettings.show_prices && (
                                                            <div className="text-xl font-bold text-primary">{service.price} <DirhamIconColored className="w-4 h-4 inline" color="primary" /></div>
                  )}
                </div>
                {service.description_ar && (
                  <p className="text-sm text-muted-foreground mb-3">
                    {language === 'ar' ? service.description_ar : service.description_en}
                  </p>
                )}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1 text-sm text-muted-foreground">
                    <Clock className="w-4 h-4" />
                    <span>{service.duration_minutes} {language === 'ar' ? 'دقيقة' : 'min'}</span>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      onClick={() => setPriceChangeService(service)}
                      size="sm"
                      variant="ghost"
                      className="text-amber-500 hover:text-amber-600"
                      title={language === 'ar' ? 'طلب تعديل السعر' : 'Request price change'}
                      data-testid={`request-price-change-${service.id}`}
                    >
                      <TrendingUp className="w-4 h-4" />
                    </Button>
                    <Button onClick={() => openServiceModal(service)} size="sm" variant="ghost">
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    {user?.role === 'admin' && (
                      <Button onClick={() => deleteService(service.id)} size="sm" variant="ghost" className="text-red-500">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            ))}
            
            {services.filter(s => s.is_active).length === 0 && (
              <div className="col-span-full text-center py-12 text-muted-foreground">
                <Scissors className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>{language === 'ar' ? 'لا توجد خدمات. أضف خدمة جديدة.' : 'No services. Add a new service.'}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'offers' && (
        <div className="space-y-4">
          <Button onClick={() => openOfferModal()} className="gap-2 bg-primary">
            <Plus className="w-4 h-4" />
            {language === 'ar' ? 'إضافة عرض' : 'Add Offer'}
          </Button>

          <div className="grid gap-4 sm:grid-cols-2">
            {offers.filter(o => o.is_active).map(offer => {
              const statusBadge = getOfferStatusBadge(offer.status);
              const isActive = new Date(offer.start_date) <= new Date() && new Date(offer.end_date) >= new Date();
              
              return (
                <Card key={offer.id} className="bg-card border-white/10 p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className={`text-xs px-2 py-1 rounded-full ${statusBadge.bg} ${statusBadge.text}`}>
                          {statusBadge.label}
                        </span>
                        {offer.status === 'approved' && isActive && (
                          <span className="text-xs px-2 py-1 rounded-full bg-green-500/20 text-green-500">
                            {language === 'ar' ? 'نشط الآن' : 'Active Now'}
                          </span>
                        )}
                        {/* Visibility indicators */}
                        {user?.role === 'admin' && (
                          <>
                            {offer.is_visible === false && (
                              <span className="text-xs px-1.5 py-0.5 rounded bg-red-500/20 text-red-400 flex items-center gap-1">
                                <EyeOff className="w-3 h-3" />
                                {language === 'ar' ? 'مخفي' : 'Hidden'}
                              </span>
                            )}
                            {offer.show_price === false && (
                              <span className="text-xs px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-400">
                                {language === 'ar' ? 'السعر مخفي' : 'Price hidden'}
                              </span>
                            )}
                          </>
                        )}
                      </div>
                      <h3 className="font-bold text-white text-lg">
                        {language === 'ar' ? offer.title_ar : offer.title_en}
                      </h3>
                    </div>
                  </div>
                  
                  {offer.description_ar && (
                    <p className="text-sm text-muted-foreground mb-3">
                      {language === 'ar' ? offer.description_ar : offer.description_en}
                    </p>
                  )}
                  
                  <div className="flex items-center gap-3 mb-3">
                                                            <span className="text-muted-foreground line-through">{offer.original_price} <DirhamIconColored className="w-3 h-3 inline" color="muted" /></span>
                    <span className="text-2xl font-bold text-primary">{offer.discount_price} <DirhamIconColored className="w-4 h-4 inline" color="primary" /></span>
                    <span className="text-sm text-green-500">
                      -{Math.round((1 - offer.discount_price / offer.original_price) * 100)}%
                    </span>
                  </div>
                  
                  {offer.service_ids?.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs text-muted-foreground mb-1">
                        {language === 'ar' ? 'الخدمات المشمولة:' : 'Included services:'}
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {offer.service_ids.map(sid => (
                          <span key={sid} className="text-xs bg-primary/20 text-primary px-2 py-1 rounded">
                            {getServiceName(sid)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      <span>{offer.start_date} - {offer.end_date}</span>
                    </div>
                    <div className="flex gap-1">
                      {(user?.role === 'admin' || offer.created_by === user?.id) && offer.status !== 'approved' && (
                        <Button onClick={() => openOfferModal(offer)} size="sm" variant="ghost">
                          <Edit2 className="w-4 h-4" />
                        </Button>
                      )}
                      {user?.role === 'admin' && (
                        <Button onClick={() => deleteOffer(offer.id)} size="sm" variant="ghost" className="text-red-500">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  {offer.created_by_name && (
                    <p className="text-xs text-muted-foreground mt-2">
                      {language === 'ar' ? 'أُنشئ بواسطة: ' : 'Created by: '}{offer.created_by_name}
                    </p>
                  )}
                </Card>
              );
            })}
            
            {offers.filter(o => o.is_active).length === 0 && (
              <div className="col-span-full text-center py-12 text-muted-foreground">
                <Tag className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>{language === 'ar' ? 'لا توجد عروض. أضف عرض جديد.' : 'No offers. Add a new offer.'}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'price_changes' && (
        <div className="bg-card border border-white/10 rounded-xl p-6">
          <PriceChangeRequestsPanel
            key={priceChangesRefreshKey}
            isAdmin={user?.role === 'admin'}
            isArabic={language === 'ar'}
            onChange={() => fetchData()}
          />
        </div>
      )}

      {/* Price change request modal (per service) */}
      <PriceChangeRequestModal
        open={!!priceChangeService}
        service={priceChangeService}
        isArabic={language === 'ar'}
        redirectAfterSave={user?.role === 'barber'}
        onClose={() => setPriceChangeService(null)}
        onSubmitted={() => {
          setPriceChangesRefreshKey((k) => k + 1);
          fetchData();
        }}
      />

      {/* Category Modal */}
      {showCategoryModal && (
        <CategoryModal
          language={language}
          isAdmin={user?.role === 'admin'}
          editingItem={editingItem}
          form={categoryForm}
          setForm={setCategoryForm}
          icons={icons}
          saving={saving}
          onSave={saveCategory}
          onClose={() => setShowCategoryModal(false)}
        />
      )}

      {/* Service Modal */}
      {showServiceModal && (
        <ServiceModal
          language={language}
          isAdmin={user?.role === 'admin'}
          editingItem={editingItem}
          form={serviceForm}
          setForm={setServiceForm}
          categories={categories}
          saving={saving}
          onSave={saveService}
          onClose={() => setShowServiceModal(false)}
        />
      )}

      {/* Offer Modal */}
      {showOfferModal && (
        <OfferModal
          language={language}
          isAdmin={user?.role === 'admin'}
          editingItem={editingItem}
          form={offerForm}
          setForm={setOfferForm}
          services={services}
          saving={saving}
          onSave={saveOffer}
          onClose={() => setShowOfferModal(false)}
        />
      )}

      {/* Preview Modal */}
      {previewItem && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setPreviewItem(null)}>
          <div className="bg-card border border-white/10 rounded-xl p-6 max-w-lg w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-primary">
                {previewType === 'offer' && (language === 'ar' ? 'معاينة العرض' : 'Offer Preview')}
                {previewType === 'category' && (language === 'ar' ? 'معاينة القسم' : 'Category Preview')}
                {previewType === 'service' && (language === 'ar' ? 'معاينة الخدمة' : 'Service Preview')}
              </h3>
              <Button variant="ghost" size="sm" onClick={() => setPreviewItem(null)}>
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* Offer Preview */}
            {previewType === 'offer' && (
              <div className="space-y-4">
                <div className="bg-gradient-to-r from-primary/20 to-primary/5 rounded-lg p-4">
                  <h4 className="text-lg font-bold text-primary mb-2">
                    {language === 'ar' ? previewItem.title_ar : previewItem.title_en}
                  </h4>
                  <p className="text-muted-foreground">
                    {language === 'ar' ? previewItem.description_ar : previewItem.description_en}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="bg-secondary/30 p-3 rounded-lg">
                    <span className="text-muted-foreground block">{language === 'ar' ? 'السعر الأصلي' : 'Original Price'}</span>
                    <span className="text-lg font-bold text-red-400 line-through">{previewItem.original_price} <DirhamIconColored className="w-4 h-4 inline" /></span>
                  </div>
                  <div className="bg-secondary/30 p-3 rounded-lg">
                    <span className="text-muted-foreground block">{language === 'ar' ? 'سعر العرض' : 'Offer Price'}</span>
                    <span className="text-lg font-bold text-green-400">{previewItem.discount_price} <DirhamIconColored className="w-4 h-4 inline" /></span>
                  </div>
                  {previewItem.discount_percentage && (
                    <div className="bg-primary/20 p-3 rounded-lg col-span-2 text-center">
                      <span className="text-primary font-bold text-xl">{previewItem.discount_percentage}% {language === 'ar' ? 'خصم' : 'OFF'}</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Clock className="w-4 h-4" />
                  <span>{language === 'ar' ? 'صالح حتى:' : 'Valid until:'} {previewItem.end_date ? new Date(previewItem.end_date).toLocaleDateString('ar-AE') : '-'}</span>
                </div>
              </div>
            )}

            {/* Category Preview */}
            {previewType === 'category' && (
              <div className="space-y-4">
                <div className="bg-secondary/30 rounded-lg p-4 text-center">
                  <div className="text-4xl mb-2">{getCategoryIcon(previewItem.icon)}</div>
                  <h4 className="text-lg font-bold">{language === 'ar' ? previewItem.name_ar : previewItem.name_en}</h4>
                  <p className="text-sm text-muted-foreground mt-2">
                    {language === 'ar' ? 'الاسم بالإنجليزية:' : 'English Name:'} {previewItem.name_en}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="bg-secondary/30 p-3 rounded-lg">
                    <span className="text-muted-foreground block">{language === 'ar' ? 'ترتيب العرض' : 'Display Order'}</span>
                    <span className="font-bold">{previewItem.display_order || 0}</span>
                  </div>
                  <div className="bg-secondary/30 p-3 rounded-lg">
                    <span className="text-muted-foreground block">{language === 'ar' ? 'الحالة' : 'Visibility'}</span>
                    <span className={`font-bold ${previewItem.is_visible ? 'text-green-400' : 'text-red-400'}`}>
                      {previewItem.is_visible ? (language === 'ar' ? 'مرئي' : 'Visible') : (language === 'ar' ? 'مخفي' : 'Hidden')}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Service Preview */}
            {previewType === 'service' && (
              <div className="space-y-4">
                <div className="bg-secondary/30 rounded-lg p-4">
                  <h4 className="text-lg font-bold text-primary">{language === 'ar' ? previewItem.name_ar : previewItem.name_en}</h4>
                  {(previewItem.description_ar || previewItem.description_en) && (
                    <p className="text-muted-foreground mt-2">
                      {language === 'ar' ? previewItem.description_ar : previewItem.description_en}
                    </p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="bg-primary/20 p-3 rounded-lg">
                    <span className="text-muted-foreground block">{language === 'ar' ? 'السعر' : 'Price'}</span>
                    <span className="text-xl font-bold text-primary">{previewItem.price} <DirhamIconColored className="w-4 h-4 inline" /></span>
                  </div>
                  <div className="bg-secondary/30 p-3 rounded-lg">
                    <span className="text-muted-foreground block">{language === 'ar' ? 'المدة' : 'Duration'}</span>
                    <span className="font-bold">{previewItem.duration_minutes} {language === 'ar' ? 'دقيقة' : 'min'}</span>
                  </div>
                  {previewItem.deposit_amount > 0 && (
                    <div className="bg-yellow-500/20 p-3 rounded-lg col-span-2">
                      <span className="text-muted-foreground block">{language === 'ar' ? 'مبلغ الوديعة' : 'Deposit Amount'}</span>
                      <span className="font-bold text-yellow-400">{previewItem.deposit_amount} <DirhamIconColored className="w-4 h-4 inline" /></span>
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className={`px-2 py-1 rounded ${previewItem.is_visible ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                    {previewItem.is_visible ? (language === 'ar' ? 'مرئي' : 'Visible') : (language === 'ar' ? 'مخفي' : 'Hidden')}
                  </span>
                  <span className={`px-2 py-1 rounded ${previewItem.show_price ? 'bg-green-500/20 text-green-400' : 'bg-orange-500/20 text-orange-400'}`}>
                    {previewItem.show_price ? (language === 'ar' ? 'السعر ظاهر' : 'Price Visible') : (language === 'ar' ? 'السعر مخفي' : 'Price Hidden')}
                  </span>
                </div>
              </div>
            )}

            {/* Common Info */}
            <div className="mt-4 pt-4 border-t border-white/10 text-sm text-muted-foreground">
              <p>{language === 'ar' ? 'أُنشئ بواسطة:' : 'Created by:'} <span className="text-white">{previewItem.created_by_name || '-'}</span></p>
              <p>{language === 'ar' ? 'تاريخ الإنشاء:' : 'Created at:'} <span className="text-white">{previewItem.created_at ? new Date(previewItem.created_at).toLocaleString('ar-AE') : '-'}</span></p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 mt-6">
              <Button
                onClick={() => {
                  if (previewType === 'offer') approveOffer(previewItem.id, true);
                  else if (previewType === 'category') approveCategory(previewItem.id, true);
                  else if (previewType === 'service') approveService(previewItem.id, true);
                  setPreviewItem(null);
                }}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                <Check className="w-4 h-4 me-2" />
                {language === 'ar' ? 'موافقة' : 'Approve'}
              </Button>
              <Button
                onClick={() => {
                  const reason = prompt(language === 'ar' ? 'سبب الرفض (اختياري):' : 'Rejection reason (optional):');
                  if (previewType === 'offer') approveOffer(previewItem.id, false, reason || '');
                  else if (previewType === 'category') approveCategory(previewItem.id, false);
                  else if (previewType === 'service') approveService(previewItem.id, false);
                  setPreviewItem(null);
                }}
                variant="destructive"
                className="flex-1"
              >
                <X className="w-4 h-4 me-2" />
                {language === 'ar' ? 'رفض' : 'Reject'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Hidden printable catalog (positioned off-screen) */}
      <div style={{ position: 'fixed', left: '-10000px', top: 0, pointerEvents: 'none' }} aria-hidden="true">
        <ServicesCatalogSheet
          ref={catalogPrintRef}
          categories={categories}
          services={services}
          offers={offers}
        />
      </div>
    </div>
  );
};

export default ServicesManagement;
